import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@cuito/ui';
import { useAuthStore } from '../../stores/authStore';
import { readCashierLocationId, readSoundEnabled, writeSoundEnabled } from '../lib/prefs';

/**
 * `/cashier/settings` 收银端设置（§3.3 有这条路由，§3.4 没列页面文件 —— 附录 A25 的裁定）。
 *
 * 本 Phase 落地三项：提示音开关（§6.2）、坐标底板入口（复用管理端已有的
 * `/locations/:id/settings`）、退出登录。
 *
 * **地点切换在顶栏**（`CashierLayout` 的 `location-select`，附录 A27）：`locationId` 是
 * 布局级状态（房间、模式徽标、全部取数都用它），藏在设置页里会让切换多两步，
 * 也会让「当前地点」出现第二个事实来源。本页因此**只有说明，没有第二个选择器**。
 *
 * 收款码上传 / 提示音量 / 全屏广告不在本 Phase 的范围（广告媒体是 Chain F；收款码归 Phase 6）——
 * 放上来就是一批本阶段无法验收的入口。
 *
 * ⚠️ 根锚点 `cashier-settings-page` **保留**：`routes.spec.tsx` 按它逐条验证六条路由都存在。
 */
export default function CashierSettingsPage() {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const [soundEnabled, setSoundEnabled] = useState(() => readSoundEnabled());
  const locationId = readCashierLocationId();

  return (
    <div data-testid="cashier-settings-page" className="flex max-w-xl flex-col gap-4">
      <h1 className="text-2xl font-bold text-gray-900">收银端设置</h1>

      <section className="flex flex-col gap-2 rounded-lg border-2 border-black p-4">
        <h2 className="text-lg font-bold">新订单提示音</h2>
        <p className="text-sm text-gray-500">地点切换在接单面板顶栏；这里只管本机的声音开关。</p>
        <Button
          data-testid="settings-sound-toggle"
          variant={soundEnabled ? 'primary' : 'secondary'}
          onClick={() => {
            const next = !soundEnabled;
            setSoundEnabled(next);
            writeSoundEnabled(next);
          }}
        >
          {soundEnabled ? '提示音：开' : '提示音：关'}
        </Button>
      </section>

      <section className="flex flex-col gap-2 rounded-lg border-2 border-black p-4">
        <h2 className="text-lg font-bold">坐标底板</h2>
        <Button
          data-testid="settings-shelf-grid"
          variant="secondary"
          disabled={locationId === null}
          onClick={() => navigate(`/locations/${locationId ?? ''}/settings`)}
        >
          打开完整地点设置
        </Button>
        {locationId === null && <p className="text-sm text-gray-500">请先在顶栏选择地点</p>}
      </section>

      <Button
        data-testid="settings-logout"
        variant="secondary"
        onClick={() => {
          logout();
          navigate('/login', { replace: true });
        }}
      >
        退出登录
      </Button>
    </div>
  );
}
